import React, { useEffect, useRef, useState } from 'react';

import { Box, Checkbox } from '@chakra-ui/react';
import { ChevronDown } from '@mesh.ai/rabbit';
import { components } from 'react-select';

import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

type Props = {
  options: { value: string; label: string }[];
  selectedOptions: { value: string; label: string }[];
  onChange: (selectedOptions: { value: string; label: string }[]) => void;
  placeholder?: string;
  label?: string;
  outsideClickToggle?: boolean;
};

// Custom Option Component with Checkboxes
const CustomOption = (props) => {
  const { data, isSelected, innerRef, innerProps, selectProps } = props;
  const isSelectAll = data.value === 'select-all';
  const allSelected = selectProps.value.length === selectProps.options.length - 1; // Exclude "Select All"
  // Prevent default behavior on mouse down to avoid triggering a blur that could close the menu
  const handleMouseDown = (e) => {
    e.preventDefault();
  };

  return (
    <Box ref={innerRef} {...innerProps} display="flex" alignItems="center" p={2} onMouseDown={handleMouseDown}>
      <Checkbox
        pointerEvents="none"
        isChecked={isSelectAll ? allSelected : isSelected}
        sx={{
          '.chakra-checkbox__control': {
            borderColor: '#0D819F', // Border color
            bg: 'transparent', // Background
            _checked: {
              bg: '#0D819F', // Checked background color
              borderColor: '#0D819F', // Checked border color
              color: 'white', // Checkmark color
            },
          },
        }}
      >
        <TextBodyMedium>{data.label}</TextBodyMedium>
      </Checkbox>
    </Box>
  );
};

// Custom MultiValueContainer for displaying comma-separated values
const CustomMultiValueContainer = ({ data, selectProps, innerProps, ...props }) => {
  const selectedValues = selectProps.value || [];
  const maxVisibleValues = 2;
  const currentIndex = selectedValues.findIndex((item) => item.value === data.value);
  const isLastVisible = currentIndex === maxVisibleValues - 1;
  const remainingCount = selectedValues.length - maxVisibleValues;
  if (currentIndex >= maxVisibleValues) {
    return null; // Hide extra selected values
  }

  return (
    <components.MultiValueContainer {...props} data={data} selectProps={selectProps} innerProps={innerProps}>
      <Box style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <TextBodyMedium bold maxWidth="67px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {data.label}
        </TextBodyMedium>
        {currentIndex < maxVisibleValues - 1 ? ',' : ''}

        {isLastVisible && remainingCount > 0 && (
          <TextBodyMedium bold color="#0D819F">
            +{remainingCount}
          </TextBodyMedium>
        )}
      </Box>
    </components.MultiValueContainer>
  );
};

const CustomDropdownIndicator = (props) => {
  const {
    selectProps: { menuIsOpen },
  } = props;
  return (
    <div
      style={{
        cursor: 'pointer',
      }}
    >
      <ChevronDown
        width={16} // Set width to 16px
        height={16} // Set height to 16px
        style={{
          transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          color: menuIsOpen ? '#004F67' : 'unset',
          transition: 'transform 200ms ease-in-out', // Smooth rotation effect
        }}
      />
    </div>
  );
};

const customComponents = {
  ClearIndicator: () => null, // Hide the clear (×) icon
  IndicatorSeparator: () => null, // Hide the separator
  DropdownIndicator: CustomDropdownIndicator,
};

export const MultiSelectWithCheckboxes = ({
  options = [],
  selectedOptions = [],
  onChange = () => {},
  placeholder = '',
  label = 'Select',
  outsideClickToggle = false,
}: Props) => {
  const labelRef = useRef(null); // Ref for the div
  const [labelWidth, setLabelWidth] = useState(70);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null); // Ref for SelectDashed

  useEffect(() => {
    const updateMenuPosition = () => {
      if (labelRef.current) {
        const rect = labelRef.current.getBoundingClientRect();
        setLabelWidth(rect.width);
      }
    };

    updateMenuPosition();
  }, []);

  useEffect(() => {
    if (selectRef.current && selectRef.current.inputRef && menuIsOpen) {
      selectRef.current.inputRef.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outsideClickToggle]);

  const optionsWithSelectAll = React.useMemo(
    () => [{ value: 'select-all', label: 'Select All' }, ...options],
    [options]
  );

  const customStyles = {
    valueContainer: (base) => ({
      ...base,
      display: 'flex',
      maxHeight: '30px',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'transparent', // Remove background color
      padding: 0, // Remove extra padding
    }),
    multiValueLabel: (base) => ({
      ...base,
      padding: 0,
      color: '#333', // Ensure text is visible
    }),
    multiValueRemove: () => ({
      display: 'none', // Hide the cross (X) button
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#0D819F', // Change color (focused: red, default: blue)
      ':hover': { color: '#0D819F' }, // Change color on hover
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: menuIsOpen ? '#0D819F' : '#333', // Change color (focused: red, default: blue)
      ':hover': { color: '#0D819F' }, // Change color on hover
    }),
    control: (base) => ({
      ...base,
      borderBottom: 'none', // Remove border-bottom
      boxShadow: 'none', // Remove focus outline
      ':hover': {
        borderBottom: 'none',
      },
      transition: 'none',
    }),
    menu: (base) => ({
      ...base,
      width: `${210 + labelWidth}px`, // Set desired width
      left: `-${10 + labelWidth}px`, // Adjust position
    }),
  };

  const handleChange = (selected: { value: string; label: string }[] | null) => {
    if (!selected) {
      onChange([]);
      return;
    }

    const isSelectAllSelected = selected.some((option) => option.value === 'select-all');
    const selectedValues = selected.map((option) => option.value);
    const allOptionsSelected = selectedValues.length === optionsWithSelectAll.length;

    if (isSelectAllSelected) {
      if (allOptionsSelected) {
        // ✅ If everything is selected and "Select All" is clicked → Unselect all
        onChange([]);
      } else {
        // ✅ Otherwise, select all options except "Select All"
        onChange(optionsWithSelectAll.filter((opt) => opt.value !== 'select-all'));
      }
    } else {
      // ✅ Normal selection behavior
      onChange(selected);
    }
  };
  const handleContainerClick = (e) => {
    if (selectRef.current) {
      if (!menuIsOpen) {
        selectRef.current.focus();
        selectRef.current.onMenuOpen();
        // setMenuIsOpen(true);
        selectRef.current.inputRef.click();
      }
    }
    e.stopPropagation();
  };

  const selectProps = {
    components: {
      Option: CustomOption,
      MultiValueContainer: CustomMultiValueContainer,
      ...customComponents,
    },
    styles: customStyles,
    onMenuOpen: () => {
      setTimeout(() => {
        setMenuIsOpen(true);
      }, 200); // Delay is necessary so that onClick logic executes before state update
    },
    onMenuClose: () => {
      setTimeout(() => {
        setMenuIsOpen(false);
      }, 200); // Delay is necessary so that onClick logic executes before state update
    },
    closeMenuOnSelect: false, // Prevent closing on option click
    blurInputOnSelect: false, // Prevent blurring (which can trigger closing)
  };

  return (
    <Box
      display="flex"
      padding="0px 8px"
      justifyContent="center"
      alignItems="center"
      gap="4px"
      borderRadius="32px"
      background={menuIsOpen ? '#CAE8F0' : '#F2F2F2'} // Change background dynamically
      border={menuIsOpen ? '1px solid #004F67' : '1px solid transparent'} // Change border dynamically
      onClick={handleContainerClick} //  Open dropdown when clicking anywhere
      cursor="pointer"
    >
      <Box ref={labelRef}>
        {' '}
        <TextBodyMedium bold color="text.g2">
          {label}:
        </TextBodyMedium>
      </Box>

      <Box style={{ width: '200px' }} onClick={(e) => e.stopPropagation()}>
        <SelectDashed
          placeholder={placeholder || 'Select'}
          value={selectedOptions}
          options={optionsWithSelectAll}
          handleChange={handleChange}
          isMulti
          isClearable
          isSearchable
          getOptionValue={undefined}
          getOptionLabel={undefined}
          selectRef={selectRef}
          hideBorder
          selectProps={selectProps}
          controlHeight="30px"
          controlPadding="0px"
        />
      </Box>
    </Box>
  );
};
